import './UserSelector.scss';

import { AntdOptionType } from '@sonar-software/sonar-ts-types';
import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SonarUser } from './SonarUser';
import { useUsers } from '@hooks';

import React, { useMemo } from 'react';
import { SonarSelector, SonarSelectorProps } from './SonarSelector';

export interface UserSelectorProps
		extends Omit<SonarSelectorProps<number | number[]>, 'options' | 'showArrow' | 'onChange'> {
	avatar?: boolean;
	onSingleChange?: (userId: number | undefined) => void;
	onMultipleChange?: (userIds: number[]) => void;
	selectionRequired?: boolean;
	withLatest?: boolean;
}

export const UserSelector: React.FC<UserSelectorProps> = ({
	avatar,
	className,
	onSingleChange,
	onMultipleChange,
	selectionRequired,
	placeholder,
	popupClassName,
	value,
	withLatest,
	...props
}) => {
	const users = useUsers(withLatest);

	const placeholderNode = useMemo(
		() => <SonarUser
			avatar={ avatar }
			placeholder={ placeholder }
			userId={ -1 } />,
		[ avatar, placeholder ]
	);

	const options = useMemo(() => {
		const userOptions: Array<AntdOptionType> = users
			?.filter((user) => !user.isLocked || user.id === value)
			.map((user) => ({
				className: classNames({
					disabled: user.isReadOnly || user.isLocked
				}),
				disabled: user.isReadOnly || user.isLocked,
				label: <SonarUser
					avatar={ avatar }
					userId={ user?.id ?? -1 } />,
				optionLabel: <SonarUser
					avatar={ avatar }
					showTooltip
					userId={ user?.id ?? -1 } />,
				searchValue: user.fullName,
				value: user.id
			}));

		if (value && !selectionRequired) {
			return [
				{
					label: <SonarUser
						avatar={ avatar }
						placeholder={ COPY_SPECIFIC.NO_OWNER }
						userId={ -1 } />,
					searchValue: '',
					value: -1
				},
				...userOptions
			];
		}

		return userOptions;
	}, [ avatar, selectionRequired, users, value ]);

	const handleOnChange = (changeValue: number | number[]) => {
		if (Array.isArray(changeValue) && onMultipleChange) {
			onMultipleChange(changeValue);

			return;
		}

		if (!onSingleChange) {
			return;
		}

		if (!changeValue || changeValue === -1) {
			onSingleChange(undefined);
		} else {
			onSingleChange(changeValue as number);
		}
	};

	return <SonarSelector
		{ ...props }
		className={ classNames('user-selector', className) }
		onChange={ handleOnChange }
		options={ options }
		placeholder={ placeholderNode }
		popupClassName={ classNames('space-conscious-owner-label', popupClassName) }
		readOnlyDisplay={ value ? <SonarUser
			placeholder={ COPY_SPECIFIC.NO_OWNER }
			showTooltip
			userId={ Number(value) } /> : null }
		showArrow
		value={ value } />;
};
